import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, Grid2, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import Button from "@mui/material/Button";
import DoneIcon from "@mui/icons-material/Done";
import { ErrorOutline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useTranslate } from "../../../../hooks/useTranslate";
import { useStudents } from "../../../../context/students";
import axiosManager from "../../../../services/axiosManager";
import Preloader from "../../../../components/Preloader";
import useStatuses from "../../../../context/status/useStatuses";
import { useGetContractEmbedded } from "../../../../hooks/useGetContractEmbedded";
import { useGetStudentContacts } from "../../../../hooks/useGetStudentContacts";
import { useGetContract } from "../../../../hooks/useGetContract";
import { useAuth } from "../../../../auth";
import EmptyContent from "../../../../components/EmptyContent";
import DocuSignEmbedded from "../../../../components/DocuSignEmbedded";
import Signees, { SigneesStatus } from "../../../applicant/EnrolmentAgreement/components/Signees";
import SummaryBox from "../../../../components/SummaryBox";
import TaskCard from "../../../../components/TaskCard";
import { useGetContractPolling } from "../../../../hooks/useGetContractPolling";

export const RenewalContract = () => {
    const { t } = useTranslate();
    const navigate = useNavigate();

    const { userId } = useAuth();
    const { renewalStatus, refetchRenewalStatus } = useStatuses();
    const { activeStudentId } = useStudents();

    const { isLoading: isLoadingContact, contacts } = useGetStudentContacts(activeStudentId);
    const contact = useMemo(() => contacts.find((contact) => contact.contactId === userId) || {}, [contacts]);
    const contactPrimary = useMemo(() => contacts.find((contact) => contact.isPrimary) || {}, [contacts]);
    const isPrimaryContact = contact?.isPrimary;

    const primarySignee = {
        id: contactPrimary.contactId,
        name: contactPrimary.displayName,
        emailAddress: contactPrimary.emailAddress,
    };
    const [secondarySignee, setSecondarySignee] = useState(null);

    const {
        isLoading, isRefetching, contract, refetch: refetchContract,
    } = useGetContract(activeStudentId, renewalStatus?.newKeyStage);

    const {
        isSigned, isPolling, contractError, startPolling, signees,
    } = useGetContractPolling({
        studentId: activeStudentId,
        hasSeveralSignees: contract?.signees?.length > 1,
        onComplete: () => {
            refetchRenewalStatus();
            if (contract?.signees?.length > 1) return;
            setTimeout(() => {
                navigate(`/renewals/payment/${activeStudentId}`);
            }, 2000);
        },
    });
    const isContractComplete = contract?.status === "Complete" || isSigned;
    const isCurrentContactSigned = (signees ?? contract?.signees)?.find((it) => it.contactId === userId)?.signingStatus === "Signed";
    const hasSeveralSignees = (signees ?? contract?.signees)?.length > 1;

    const showSignViaEmail = !!contract && !isPrimaryContact;
    const embeddedBody = isPrimaryContact ? {
        emailAddress: contact.emailAddress,
        name: contact.displayName,
        contactId: userId,
    } : null;

    const {
        isLoading: isLoadingEmbedded,
        contractEmbedded,
    } = useGetContractEmbedded(isPrimaryContact ? contract?.contractId : null, activeStudentId, embeddedBody);

    const showDocAgreement = !!contract && isPrimaryContact && !!contractEmbedded;

    const { mutateAsync: issueAgreement, isPending: isIssuingAgreement } = useMutation({
        mutationFn: () => axiosManager.post$(`/parent/api/Students/${activeStudentId}/Contracts?voidExisting=true&keyStage=${renewalStatus?.newKeyStage}`, {
            ContractType: renewalStatus?.contractType,
            KeyStage: renewalStatus?.newKeyStage,
            StudentId: activeStudentId,
            PrimaryContact: {
                ContactId: contactPrimary.contactId,
                Name: contactPrimary.displayName,
                EmailAddress: contactPrimary.emailAddress,
            },
            SecondaryContact: secondarySignee ? {
                ContactId: secondarySignee.id,
                Name: secondarySignee.name,
                EmailAddress: secondarySignee.emailAddress,
            } : undefined,
        }),
    });

    const onProceedIssue = async () => {
        try {
            await issueAgreement();
            refetchContract();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="page-content">
            <div className="student-head-section" />
            <Grid2 container spacing={2} className="min-head-section-height">
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }} className="text-white">
                    <Breadcrumbs
                        breadcrumbs={[
                            { label: t("navigation.home"), href: `/home/${activeStudentId}` },
                            { label: t("renewals.title"), href: `/renewals/${activeStudentId}` },
                            { label: t("renewalSchoolContract.title"), current: true },
                        ]}
                    />
                    <Typography variant="h1" className="text-white">
                        {t("renewalSchoolContract.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4 text-white" component="div">
                        {t("renewalSchoolContract.subtitle")}
                        <a
                            className="text-white"
                            href={`mailto:${t("renewalSchoolContract.email")}`}
                        >
                            {t("renewalSchoolContract.email")}
                        </a>
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
                    <TaskCard
                        className="payments__task-card"
                        title={t("enrolmentAgreement.requiredSigneesStatus")}
                    >
                        <Preloader isLoading={isLoading || isLoadingContact} size={20}>
                            <Preloader isLoading={isPolling} size={20} overflow showInitiallyOverflow>
                                <div className="w-full mb-2">
                                    <SigneesStatus
                                        signees={signees ?? contract?.signees}
                                        contacts={contacts}
                                        studentId={activeStudentId}
                                        contractId={contract?.contractId}
                                        isCard
                                        hideReEmail
                                    />
                                </div>
                            </Preloader>
                        </Preloader>
                    </TaskCard>
                </Grid2>
            </Grid2>
            <div className="">
                <Preloader isLoading={isLoading || isRefetching || isLoadingContact || isLoadingEmbedded}>
                    {isContractComplete && (
                        <div className="checkout-status checkout-status-success">
                            <span className="checkout-status__icon checkout-status__icon-success">
                                <DoneIcon />
                            </span>
                            <Typography variant="body1" component="div">
                                {t("renewalSchoolContract.succeededMessage")}
                            </Typography>
                        </div>
                    )}
                    {isPolling && (
                        <div className="checkout-status checkout-status-loading">
                            <div className="checkout-status__icon-loading">
                                <CircularProgress size={32} />
                            </div>
                            <Typography variant="body1" component="div">
                                {t("renewalSchoolContract.loadingMessage")}
                            </Typography>
                        </div>
                    )}
                    {contractError && (
                        <div className="checkout-status checkout-status-error">
                            <span className="checkout-status__icon checkout-status__icon-error">
                                <ErrorOutline />
                            </span>
                            <Typography variant="body1" component="div">
                                {t("renewalSchoolContract.errorMessage")}
                            </Typography>
                        </div>
                    )}
                    <EmptyContent data={showSignViaEmail ? [] : {}} text={t("enrolmentAgreement.signVieEmail")}>
                        {(showDocAgreement && !isContractComplete && !isSigned && !contractError && !isPolling) && (
                            <>
                                {hasSeveralSignees && isCurrentContactSigned ? (
                                    <div className="checkout-status checkout-status-loading">
                                        <span className="checkout-status__icon-loading">
                                            <HourglassBottomIcon className="text-primary" />
                                        </span>
                                        <Typography variant="body1" component="div">
                                            {t("renewalSchoolContract.severalSignedMessage")}
                                        </Typography>
                                    </div>
                                ) : (
                                    <>
                                        <Typography variant="h2" className="text-primary">
                                            {t(renewalStatus?.contractType === "Rolling" ? "renewalSchoolContract.rollingContract" : "renewalSchoolContract.annualContract")}
                                        </Typography>
                                        <div className="mt-8 flex justify-center">
                                            <DocuSignEmbedded
                                                signingUrl={contractEmbedded?.url}
                                                onSigned={startPolling}
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        {(!showDocAgreement && !isContractComplete && !isSigned && !contractError && !isPolling) && (
                            <>
                                <Preloader isLoading={isLoading || isRefetching || isLoadingContact} size={30}>
                                    <Signees
                                        primarySignee={primarySignee}
                                        secondarySignee={secondarySignee}
                                        onChangeSecondarySignee={setSecondarySignee}
                                        contacts={contacts}
                                    />
                                </Preloader>
                                <SummaryBox
                                    className="mb-12"
                                    contentClassName="p-8 flex gap-4 flex-column align-center justify-center"
                                    isFormBox
                                >
                                    <Typography variant="h4" component="div" c className="text-primary text-center">
                                        {t("renewalSchoolContract.issueYourContractAgreement")}
                                    </Typography>
                                    <Typography variant="body1" component="div" className="text-center">
                                        {t("renewalSchoolContract.issueYourContractAgreementSub")}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        className="button-accent"
                                        onClick={onProceedIssue}
                                        loading={isIssuingAgreement}
                                    >
                                        {t("renewalSchoolContract.issueAgreement")}
                                    </Button>
                                </SummaryBox>
                                <div className="mt-8 mb-12 flex justify-end" />
                            </>
                        )}
                    </EmptyContent>
                </Preloader>
            </div>
        </div>
    );
};
